import { useState, useEffect } from 'react';
import { X, Server, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { api } from '../api/client';
import type { SessionInfo } from '../api/types';
import './ResetModal.css';
import './CloneDialog.css';

interface Props {
  sessionName: string;
  source: SessionInfo | null;
  onClose: () => void;
  /** Called after a successful move with the target node name. */
  onMoved?: (node: string) => void;
}

type Phase = 'idle' | 'running' | 'error' | 'aborted' | 'pending';

function nodeLabel(n?: string): string {
  return !n || n === 'local' ? 'mac' : n;
}

/**
 * Move an agent to another federation node (Cloud /migrate). A MOVE, not a
 * copy: the bot-home + transcript are rsync'd to the target and deleted on the
 * source (Alex 2026-06-23: "der läuft jetzt auf arkserver"). Slow for large
 * bot-homes, so the dialog blocks with a spinner until Cloud reports done.
 */
export function MoveDialog({ sessionName, source, onClose, onMoved }: Props) {
  const [nodes, setNodes] = useState<Array<{ name: string; is_self: boolean }>>([]);
  const [localNode, setLocalNode] = useState<string>('mac');
  const [target, setTarget] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorText, setErrorText] = useState('');

  // Current node: the session's node, falling back to the cluster's local.
  const current = source?.node && source.node !== 'local' ? source.node : localNode;

  useEffect(() => {
    api.cluster.nodes()
      .then((r) => {
        setLocalNode(r.local || 'mac');
        setNodes(r.nodes.map((n) => ({ name: n.name, is_self: n.is_self })));
      })
      .catch(() => {});
  }, []);

  const targets = nodes.filter((n) => n.name !== current);

  const submit = async () => {
    if (!target || phase === 'running') return;
    setPhase('running');
    setErrorText('');
    try {
      const r = await api.sessions.migrate(sessionName, target);
      // Cloud's contract is status-based — there is NO `ok`/`node` field.
      if (r?.status === 'migrated') {
        onMoved?.(target);
        onClose();
      } else if (r?.status === 'aborted_source_intact') {
        // The HARD verify failed → Cloud kept the source intact + resumed it.
        // Nothing lost; surface it as a safe abort, not a scary failure.
        setPhase('aborted');
        setErrorText(r.error || r.warning || 'Ziel kam nicht hoch — Quelle ist unverändert geblieben.');
      } else {
        setPhase('error');
        setErrorText(r?.error || 'Verschieben fehlgeschlagen.');
      }
    } catch (e) {
      // A large bot-home can outlast nginx's proxy_read_timeout → the fetch
      // sees a 504/abort while the move FINISHES server-side (verify-gate
      // protects the source either way). So don't cry "failed" — tell the user
      // it's still running and to check the session list (Cloud 2026-06-23).
      const msg = e instanceof Error ? e.message : '';
      if (/504|timeout|timed out|network|gateway/i.test(msg)) {
        setPhase('pending');
      } else {
        setPhase('error');
        setErrorText(msg || 'Verschieben fehlgeschlagen.');
      }
    }
  };

  return (
    <div className="reset-modal-backdrop" onClick={onClose}>
      <div className="reset-modal clone-modal" onClick={(e) => e.stopPropagation()}>
        <header className="reset-modal-header">
          <h2><Server size={18} /> Agent verschieben — {sessionName}</h2>
          <button className="reset-modal-close" onClick={onClose}><X size={16} /></button>
        </header>

        <div className="reset-modal-warning">
          <AlertTriangle size={14} />
          <span>
            <strong>Move, kein Copy.</strong> Bot-Home + komplette History werden auf die
            Ziel-Node kopiert und auf der Quelle <strong>gelöscht</strong> — aber erst, wenn
            der Agent auf dem Ziel bestätigt läuft (schlägt das fehl, bleibt die Quelle
            unverändert). Ein gerade laufender Turn wird unterbrochen; die Konversation bis
            zum letzten Speicherpunkt kommt sauber wieder. Bei großer History dauert der
            rsync einige Minuten.
          </span>
        </div>

        <div className="move-route">
          <span className="move-node move-node-from">{nodeLabel(current)}</span>
          <ArrowRight size={16} />
          <span className="move-node move-node-to">{target ? nodeLabel(target) : '?'}</span>
        </div>

        <label className="reset-modal-label clone-inline">
          Ziel-Node
          <select className="reset-modal-input" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">— Node wählen —</option>
            {targets.map((n) => (
              <option key={n.name} value={n.name}>{nodeLabel(n.name)}{n.is_self ? ' (hier)' : ''}</option>
            ))}
          </select>
        </label>

        {phase === 'running' && (
          <div className="clone-hint">
            Verschiebe nach {nodeLabel(target)}… Agent wird suspendiert, kopiert, auf der
            Ziel-Node gestartet und (nach bestätigtem Start) auf der Quelle entfernt. Kann
            bei großer History einige Minuten dauern — bitte nicht schließen.
          </div>
        )}
        {phase === 'aborted' && (
          <div className="clone-hint clone-hint-warn">
            Abgebrochen — der Agent kam auf {nodeLabel(target)} nicht hoch. <strong>Die Quelle
            ist unverändert</strong> und läuft weiter. {errorText}
          </div>
        )}
        {phase === 'pending' && (
          <div className="clone-hint">
            Der Server hat länger gebraucht als die Verbindung offen blieb — der Move <strong>läuft
            serverseitig weiter</strong>. Prüf in ein paar Minuten die Sessionliste: der Agent
            taucht dann auf {nodeLabel(target)} auf. Geht etwas schief, bleibt die Quelle erhalten.
          </div>
        )}
        {phase === 'error' && <div className="clone-hint clone-hint-warn">{errorText}</div>}

        <div className="reset-modal-actions">
          <button className="reset-modal-cancel" onClick={onClose}>
            {phase === 'aborted' || phase === 'pending' || phase === 'error' ? 'Schließen' : 'Abbrechen'}
          </button>
          {phase !== 'aborted' && phase !== 'pending' && (
            <button className="reset-modal-submit" onClick={submit} disabled={!target || phase === 'running'}>
              {phase === 'running' ? <><Loader2 size={14} className="clone-spin" /> Verschiebe…</> : 'Verschieben'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
